import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { PANELS } from "../data/site";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1565608438257-fac3c27beb36?w=1200&q=80";

function onImgError(e) {
  if (e.target.dataset.fallbackApplied) return;
  e.target.dataset.fallbackApplied = "1";
  e.target.src = FALLBACK_IMG;
}

export default function Panels() {
  const [idx, setIdx] = useState(null);

  useEffect(() => {
    if (idx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIdx(null);
      if (e.key === "ArrowRight")
        setIdx((i) => (i + 1) % PANELS.length);
      if (e.key === "ArrowLeft")
        setIdx((i) => (i - 1 + PANELS.length) % PANELS.length);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [idx]);

  return (
    <section
      id="razdjelnici"
      data-testid="panels-section"
      className="relative py-24 md:py-32 bg-surface border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <div className="md:col-span-7 reveal">
            <div className="text-xs uppercase tracking-widest2 text-brand flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-brand" />
              Razdjelnici
            </div>
            <h2 className="font-display font-black text-white tracking-tightest leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Preciznost koja se
              <br />
              <span className="text-brand">vidi golim okom.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-ink-muted leading-relaxed md:pb-3 reveal">
            Galerija naših realiziranih elektro razdjelnika. Čisto vođenje
            kablova, jasne oznake i poštivanje strogih standarda — kvaliteta
            koja se ne vidi samo kad sve radi.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {PANELS.map((src, i) => (
            <button
              key={src}
              data-testid={`panel-thumb-${i}`}
              onClick={() => setIdx(i)}
              className="group relative aspect-[3/4] overflow-hidden border border-white/10 hover:border-brand transition-colors reveal"
              style={{ animationDelay: `${(i % 4) * 0.05}s` }}
            >
              <img
                src={src}
                alt={`Elektro razdjelnik ${i + 1}`}
                loading="lazy"
                onError={onImgError}
                className="w-full h-full object-cover zoom-img"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/0 transition-colors" />
              <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="w-10 h-10 grid place-items-center bg-brand text-white">
                  <ZoomIn size={16} />
                </span>
              </div>
              <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest2 text-white/90 font-bold">
                #{(i + 1).toString().padStart(2, "0")}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {idx !== null && (
        <div
          data-testid="panels-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md grid place-items-center p-4"
          onClick={() => setIdx(null)}
        >
          <button
            data-testid="panels-lightbox-close"
            onClick={() => setIdx(null)}
            className="absolute top-6 right-6 w-11 h-11 grid place-items-center bg-black/70 border border-white/10 hover:border-brand hover:text-brand text-white"
            aria-label="Zatvori"
          >
            <X size={18} />
          </button>
          <button
            data-testid="panels-prev"
            onClick={(e) => {
              e.stopPropagation();
              setIdx((i) => (i - 1 + PANELS.length) % PANELS.length);
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center bg-black/70 border border-white/10 hover:border-brand hover:text-brand text-white"
            aria-label="Prethodna"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid="panels-next"
            onClick={(e) => {
              e.stopPropagation();
              setIdx((i) => (i + 1) % PANELS.length);
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center bg-black/70 border border-white/10 hover:border-brand hover:text-brand text-white"
            aria-label="Sljedeća"
          >
            <ChevronRight size={22} />
          </button>
          <img
            src={PANELS[idx]}
            alt={`Elektro razdjelnik ${idx + 1}`}
            onError={onImgError}
            className="max-w-[92vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-widest2 text-white/70">
            {idx + 1} / {PANELS.length}
          </div>
        </div>
      )}
    </section>
  );
}
